import { toast } from 'react-toastify';
import { DreamHomePageTab, PageName } from './enums';

/**
 * Checks if an array is valid (not null, undefined, or empty).
 * @param {array} arr - The array to check.
 * @returns {boolean} True if the array is valid, false otherwise.
 */
export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

/**
 * Display a toast notification based on the message type.
 *
 * @param {string} type The type of toast to display ('success' or 'error').
 * @param {string} message The message to display in the toast.
 * @param {Object} [options] Additional options for customizing the toast appearance and behavior.
 */
export const showToaster = (type, message, options = {}) => {
  const defaultOptions = {
    position: 'top-center',
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'light',
    ...options, // Merge any additional options passed to the function
  };

  // Display the toast based on the type
  if (type === 'success') {
    toast.success(message, defaultOptions);
  } else if (type === 'error') {
    toast.error(message, defaultOptions);
  } else {
    console.warn('showToast called with invalid type. Expected "success" or "error".');
  }
};

/**
 * Returns a capitalized string.
 * @param {string} str - The string to capitalize.
 * @returns {string|null} The capitalized string, or null if input is invalid.
 */
export const capitalizeFirstLetter = str => {
  if (typeof str !== 'string' || str.length === 0) {
    return null;
  }

  return (str.charAt(0).toUpperCase() + str.slice(1)).trim();
};
export const bytesToMB = bytes => {
  if (!bytes || isNaN(bytes)) {
    return 'N/A'; // Return an empty string or a placeholder if bytes is invalid
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// Helper to generate a random password
export const generateRandomPassword = (length = 12) => {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+';
  let password = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  return password;
};

// Helper to strip the extension from a file name
export const getBaseName = (fullName = '') => {
  // If there's no dot, return the full name. Otherwise, remove everything from the last dot onward.
  return fullName.replace(/\.[^/.]+$/, '');
};
export const extractFileExtension = filename => {
  // Check if file is valid and has a 'name' property
  if (!filename) {
    return null; // Return null if file or file.name is not defined
  }

  // Extract and return the file extension
  return filename.substring(filename.lastIndexOf('.') + 1).toLowerCase();
};

export const handleDownloadFile = file => {
  // Create a temporary <a> element
  const link = document.createElement('a');
  link.href = file.fileLink;

  // Use file.fileName or file.name as the default download name
  // Fallback to a generic name if none is available
  link.download = file.fileName || file.name || 'download';

  // Append to the DOM, trigger click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
/**
 * Converts a Unix timestamp (in seconds) to a JavaScript Date object.
 *
 * @param {number} timestamp - Unix timestamp in seconds.
 * @returns {Date|null} A Date object if valid, else null.
 */
export const convertUnixTimestamp = timestamp => {
  if (timestamp == null) return null;
  return new Date(timestamp * 1000);
};

// Helpers to format / parse
export const formatNumberWithCommas = value => {
  if (value == null || value === '') return '';
  const num = Number(value);
  return isNaN(num) ? '' : '£ ' + num.toLocaleString('en-GB');
};

export const parseNumberFromFormatted = value => value.replace(/\D/g, '');

export const getUserType = user => user?.attributes?.profile?.publicData?.userType;

export const deduplicateAndSplitColumns = columns => {
  const seen = new Set();

  // Flatten and deduplicate by label
  const deduped = columns.flat().filter(item => {
    if (seen.has(item.label)) return false;
    seen.add(item.label);
    return true;
  });

  // Split the array into two nearly equal parts
  const midpoint = Math.ceil(deduped.length / 2);
  return [deduped.slice(0, midpoint), deduped.slice(midpoint)];
};

export const isFinalStepCompletedForBriefs = listing => {
  return listing?.attributes?.publicData?.isFinalStepCompleted;
};

export const getBriefPage = listing => {
  return listing?.attributes?.publicData?.identifiedPlot
    ? { page: PageName.DREAM_HOME_PAGE, params: { tab: DreamHomePageTab.DESIGN_BRIEF } }
    : { page: PageName.PROPERTY_DEVELOPER_PAGE, params: { tab: DreamHomePageTab.DESIGN_BRIEF } };
};

export const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

export const hasCurrentUser = currentUser => {
  if (!currentUser || !currentUser?.id) {
    return false;
  }
  return true;
};

export const filterListingsWithoutFinalStepKey = listings => {
  return listings.filter(listing => {
    const publicData = listing?.attributes?.publicData || {};
    return !publicData.hasOwnProperty('isFinalStepCompleted');
  });
};

export const getOwnMoodboardListings = user => {
  const imagesLinked = user?.attributes?.profile?.publicData?.imagesLiked;
  return imagesLinked ? imagesLinked.map(i => i?.redirectInfo?.id) : [];
};

export const sustainabilityOptions = [
  { key: 'ashp', label: 'Air Source Heat Pumps (ASHP)' },
  { key: 'gshp', label: 'Ground Source Heat Pumps (GSHP)' },
  { key: 'mvhr', label: 'Mechanical Ventilation Units (MVHR)' },
  { key: 'solar', label: 'Solar Roof Panels' },
];

/**
 * Filters builder contents to include only those that match the variationId
 * from filtered trusted suppliers (based on bannerId).
 *
 * @param {Object} builderContents - Builder content object keyed by slug.
 * @param {Object} trustedSuppliers - Trusted suppliers keyed by slug.
 * @param {Array<string>} appliedFilters - Combined list of selected filter slugs.
 * @returns {Object} - Filtered builder content.
 */
export function filterBuilderContentByVariationId(
  builderContents,
  trustedSuppliers,
  appliedFilters
) {
  // Flatten suppliers into an array of supplier objects
  const allSuppliers = Object.values(trustedSuppliers || {});

  // Get suppliers that match the selected filters
  const matchedSuppliers = allSuppliers.filter(supplier => {
    const supplierSlug = supplier?.supplier_type?.slug;

    const coverageSlugs = supplier?.geographical_coverages?.map(gc => gc.slug) || [];

    return (
      appliedFilters.includes(supplierSlug) ||
      coverageSlugs.some(slug => appliedFilters.includes(slug))
    );
  });

  // Extract bannerIds from those matched suppliers
  const matchedBannerIds = matchedSuppliers
    .map(supplier => String(supplier.bannerId))
    .filter(Boolean);
  // Filter builderContents based on matching variationId
  const filtered = Object.entries(builderContents).filter(([_, content]) => {
    const variationId = String(content?.content?.variationId);
    return matchedBannerIds.includes(variationId);
  });

  return Object.fromEntries(filtered);
}

export function normalizeBuilderContents(entries) {
  return Object.fromEntries(entries.map(([slug, page]) => [slug, { content: page }]));
}

export const groupByListingId = docs => {
  if (!Array.isArray(docs)) return {};
  return docs.reduce((acc, doc) => {
    const id = doc.listingId;
    if (!acc[id]) acc[id] = [];
    acc[id].push(doc);
    return acc;
  }, {});
};
export const sortVersions = versions =>
  versions.sort((a, b) => {
    if (a.isPostEditVersion && !b.isPostEditVersion) return -1;
    if (!a.isPostEditVersion && b.isPostEditVersion) return 1;
    const aTime = a?.createdAt?._seconds || 0;
    const bTime = b?.createdAt?._seconds || 0;
    return bTime - aTime;
  });

export const getLabelFromKey = (key, options) => {
  const found = options.find(opt => opt.key === key);
  return found?.label || key;
};
